"use client";

import Image from "next/image";
import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/motion/Reveal";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);

interface AboutStoryImageProps {
  src: string;
  alt: string;
  className?: string;
}

export function AboutStoryImage({ src, alt, className }: AboutStoryImageProps) {
  const reduced = useReducedMotion();
  const sectionRef = useRef<HTMLDivElement>(null);
  const imageWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reduced) return;

    const section = sectionRef.current;
    const imageWrap = imageWrapRef.current;
    if (!section || !imageWrap) return;

    const ctx = gsap.context(() => {
      gsap.to(imageWrap, {
        yPercent: 12,
        ease: "none",
        scrollTrigger: {
          trigger: section,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.8,
        },
      });
    }, section);

    return () => ctx.revert();
  }, [reduced]);

  return (
    <Reveal className={cn("relative", className)} amount={0.2}>
      <div
        ref={sectionRef}
        className="relative aspect-[4/5] overflow-hidden rounded-3xl border border-[var(--charity-green)]/15 shadow-[0_24px_64px_rgba(26,77,46,0.12)] md:aspect-[3/4]"
      >
        <div
          ref={imageWrapRef}
          className="absolute inset-0 h-[115%] w-full will-change-transform"
        >
          <Image
            src={src}
            alt={alt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 100vw, 45vw"
          />
        </div>
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--charity-green-dark)]/40 via-transparent to-transparent"
          aria-hidden
        />
      </div>
    </Reveal>
  );
}
